import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

const SCROLL_SPEED_PIXELS_PER_SECOND = 120;
const SCROLL_DELAY_AFTER_HOVER = 0.3;

const contentClassName = 'scroll-on-hover-text-content';

export const scrollTextOnHoverStyles = {
  '@media (prefers-reduced-motion: no-preference)': {
    [`&:hover .${contentClassName}`]: {
      width: 'max-content',
      transform: 'translateX(var(--scroll-offset))',
      transition: `transform var(--scroll-duration) linear ${SCROLL_DELAY_AFTER_HOVER}s`,
    },
  },
} as const;

const CONTAINER_STYLES = {
  display: 'block',
  overflow: 'hidden',
  [`& .${contentClassName}`]: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
} as const;

interface Props {
  readonly children: string;
  readonly sx?: SxProps<Theme>;
}

/**
 * Single-line text clipped with an ellipsis that scrolls horizontally when hovered.
 */
export function ScrollOnHoverText({ children, sx }: Props) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const contentRef = useRef<HTMLSpanElement>(null);
  const [scrollDistance, setScrollDistance] = useState(0);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const observer = new ResizeObserver(() =>
      setScrollDistance(Math.max(0, content.scrollWidth - container.clientWidth)),
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [children]);

  return (
    <Box
      component="span"
      ref={containerRef}
      title={scrollDistance > 0 ? children : undefined}
      style={
        {
          '--scroll-offset': `${-scrollDistance}px`,
          '--scroll-duration': `${scrollDistance / SCROLL_SPEED_PIXELS_PER_SECOND}s`,
        } as React.CSSProperties
      }
      sx={[CONTAINER_STYLES, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <span ref={contentRef} className={contentClassName}>
        {children}
      </span>
    </Box>
  );
}
